import { supabase } from '@/integrations/supabase/client';
import { isNativeApp, nativePlatform } from '@/lib/nativeApp';
import { STORE_PRODUCTS } from '../../supabase/functions/_shared/appStore';

/**
 * Store subscriptions in the app: the bridge to the native StorePurchases
 * plugin (mobile/plugins/store-purchases: StoreKit on iOS, Play Billing on
 * Android) and the hand-off to the server, which reads each purchase back from
 * Apple or Google and applies the plan to the account
 * (supabase/functions/app-store-sync, google-play-sync).
 *
 * On Android a subscription's purchase token plays the part of the original
 * transaction id.
 */

export interface StoreProduct {
  id: string;
  displayName: string;
  /** Localised by the App Store, e.g. "28,50 €". */
  displayPrice: string;
  price: number;
  currencyCode: string;
  periodUnit?: 'day' | 'week' | 'month' | 'year';
  periodValue?: number;
}

export interface StoreEntitlement {
  productId: string;
  transactionId: string;
  originalTransactionId: string;
  expirationDate?: string;
  appAccountToken?: string;
  isUpgraded?: boolean;
}

interface StorePurchasesPlugin {
  getProducts(options: { productIds: string[] }): Promise<{ products: StoreProduct[] }>;
  purchase(options: { productId: string; appAccountToken: string }): Promise<{
    status: 'purchased' | 'pending' | 'cancelled';
    entitlement?: StoreEntitlement;
  }>;
  currentEntitlements(): Promise<{ entitlements: StoreEntitlement[] }>;
  restore(): Promise<{ entitlements: StoreEntitlement[] }>;
  manageSubscriptions(): Promise<void>;
  addListener?(event: 'transactionUpdated', listener: (entitlement: StoreEntitlement) => void): unknown;
}

export type StorePlanSlug = 'standard' | 'premium' | 'pro';
export type StorePeriod = 'monthly' | 'yearly';

export const STORE_PRODUCT_IDS = Object.keys(STORE_PRODUCTS);
export const storeProductId = (plan: StorePlanSlug, period: StorePeriod) => `io.cctvplanner.app.${plan}.${period}`;

function plugin(): StorePurchasesPlugin | null {
  if (!isNativeApp()) return null;
  const w = window as unknown as { Capacitor?: { Plugins?: { StorePurchases?: StorePurchasesPlugin } } };
  return w.Capacitor?.Plugins?.StorePurchases ?? null;
}

export const storePurchasesAvailable = (): boolean => plugin() !== null;

export async function loadStoreProducts(): Promise<StoreProduct[]> {
  const p = plugin();
  if (!p) return [];
  const { products } = await p.getProducts({ productIds: STORE_PRODUCT_IDS });
  return products;
}

export type SyncOutcome =
  | { ok: true; plan: string | null }
  /** `other_account`: the subscription was bought from a different CCTVplanner account. */
  | { ok: false; reason: string };

export async function syncStoreTransaction(transactionId: string): Promise<SyncOutcome> {
  const { data, error } = nativePlatform() === 'android'
    ? await supabase.functions.invoke('google-play-sync', { body: { purchaseToken: transactionId } })
    : await supabase.functions.invoke('app-store-sync', { body: { transactionId } });
  if (error) {
    let reason = 'sync_failed';
    try {
      const body = await (error as { context?: Response }).context?.json();
      if (typeof body?.error === 'string') reason = body.error;
    } catch { /* the error body was not JSON */ }
    return { ok: false, reason };
  }
  return { ok: true, plan: (data as { plan?: string } | null)?.plan ?? null };
}

export type PurchaseResult =
  | { status: 'purchased'; sync: SyncOutcome }
  | { status: 'pending' }
  | { status: 'cancelled' };

/** Buy a subscription for the signed-in account (its id rides on the
 *  purchase as appAccountToken) and apply it on the server straight away. */
export async function purchaseStoreProduct(productId: string, accountId: string): Promise<PurchaseResult> {
  const p = plugin();
  if (!p) throw new Error('Store purchases are only available in the app');
  const result = await p.purchase({ productId, appAccountToken: accountId });
  if (result.status === 'pending') return { status: 'pending' };
  if (result.status !== 'purchased' || !result.entitlement) return { status: 'cancelled' };
  return { status: 'purchased', sync: await syncStoreTransaction(result.entitlement.originalTransactionId) };
}

async function syncEach(entitlements: StoreEntitlement[]): Promise<SyncOutcome[]> {
  const ids = [...new Set(entitlements.map((e) => e.originalTransactionId))];
  const outcomes: SyncOutcome[] = [];
  for (const id of ids) outcomes.push(await syncStoreTransaction(id));
  return outcomes;
}

/** "Restore purchases": every subscription this Apple ID or Google account
 *  holds, re-applied. */
export async function restoreStorePurchases(): Promise<SyncOutcome[]> {
  const p = plugin();
  if (!p) return [];
  const { entitlements } = await p.restore();
  return syncEach(entitlements);
}

export async function manageStoreSubscriptions(): Promise<void> {
  await plugin()?.manageSubscriptions();
}

/** "Manage billing" inside the app. A plan billed by the store opens its
 *  subscription settings (Apple's sheet, Google Play's page); anything else
 *  shows the in-app plans page, never the website's payment portal (store
 *  rules). */
export async function openAppBilling(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    const { data } = await supabase.from('subscriptions').select('*').eq('user_id', user.id).maybeSingle();
    const source = (data as { billing_source?: string } | null)?.billing_source;
    if (source === 'app_store' || source === 'play') {
      await manageStoreSubscriptions();
      return;
    }
  }
  window.location.assign('/pricing');
}

/** In the app, at launch: re-apply what the store says this Apple ID or
 *  Google account is entitled to, and every change it reports while the app
 *  runs.
 *  Covers a purchase whose sync never reached the server and a missed
 *  notification. Failures (signed out, another account) are left alone. */
export function startStoreEntitlementSync(onSynced?: () => void): void {
  const p = plugin();
  if (!p) return;
  const sync = (entitlements: StoreEntitlement[]) =>
    syncEach(entitlements).then((outcomes) => { if (outcomes.some((o) => o.ok)) onSynced?.(); }).catch(() => {});
  p.currentEntitlements().then(({ entitlements }) => sync(entitlements)).catch(() => {});
  p.addListener?.('transactionUpdated', (entitlement) => { void sync([entitlement]); });
}
